import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import IntervalInput, {
  intervalToLatex,
  parseIntervalInput,
} from "../../../../components/inputs/IntervalInput";

const RELATIONS = [
  { key: "gt", latex: ">", inclusive: false, greater: true },
  { key: "lt", latex: "<", inclusive: false, greater: false },
  { key: "ge", latex: "\\ge", inclusive: true, greater: true },
  { key: "le", latex: "\\le", inclusive: true, greater: false },
];

function randInt(min, max, excludeZero = false) {
  let value;
  do {
    value = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (excludeZero && value === 0);
  return value;
}

function formatLinearLatex(a, b) {
  const aPart = a === 1 ? "" : a === -1 ? "-" : String(a);
  const bPart = b === 0 ? "" : b > 0 ? ` + ${b}` : ` - ${Math.abs(b)}`;
  return `${aPart}x${bPart}`;
}

function solution(data) {
  const relation = RELATIONS.find((item) => item.key === data.relation);
  const greater = data.a > 0 ? relation.greater : !relation.greater;
  return {
    boundary: data.boundary,
    inclusive: relation.inclusive,
    greater,
  };
}

function solutionLatex(data) {
  const result = solution(data);
  if (result.greater) {
    return `${result.inclusive ? "[" : "("}${result.boundary},\\;\\infty)`;
  }
  return `(-\\infty,\\;${result.boundary}${result.inclusive ? "]" : ")"}`;
}

function matchesSolution(parsed, expected) {
  if (parsed?.kind !== "intervals" || parsed.intervals.length !== 1) return false;
  const interval = parsed.intervals[0];

  if (expected.greater) {
    return interval.right === Infinity
      && Math.abs(interval.left - expected.boundary) < 1e-9
      && interval.leftClosed === expected.inclusive;
  }

  return interval.left === -Infinity
    && Math.abs(interval.right - expected.boundary) < 1e-9
    && interval.rightClosed === expected.inclusive;
}

export function generateTask(options = {}) {
  const a = randInt(-6, 6, true);
  const b = randInt(-10, 10);
  const boundary = randInt(-7, 7);
  const relation = RELATIONS[randInt(0, RELATIONS.length - 1)];
  const targetValue = a * boundary + b;

  return {
    type: "LinearFunctionInequalityTask",
    data: {
      a,
      b,
      boundary,
      targetValue,
      relation: relation.key,
      mentionLinearFunction: options.mentionLinearFunction !== false,
    },
  };
}

export function renderTask({ data }) {
  const relation = RELATIONS.find((item) => item.key === data.relation);
  return (
    <div style={{ marginTop: 6 }}>
      <div>
        Dana jest {data.mentionLinearFunction !== false ? "funkcja liniowa" : "funkcja"}:
      </div>
      <BlockMath math={`f(x)=${formatLinearLatex(data.a, data.b)}`} />
      <div>
        Wyznacz argumenty, dla których{" "}
        <InlineMath math={`f(x)${relation.latex}${data.targetValue}`} />.
      </div>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div style={{ marginTop: 10 }}>
      <IntervalInput
        value={userAnswer || ""}
        onChange={onChange}
        disabled={disabled}
        label="x"
        previewWithX
        placeholder="np. (-∞; 2) lub [3; ∞)"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw;
  if (!raw) return false;
  return matchesSolution(parseIntervalInput(raw), solution(data));
}

export function renderAnswer({ data }) {
  return <InlineMath math={`x\\in ${solutionLatex(data)}`} />;
}

export function renderExplanation({ data }) {
  const relation = RELATIONS.find((item) => item.key === data.relation);
  const rightSide = data.targetValue - data.b;
  const result = solution(data);
  const finalRelation = result.greater
    ? (result.inclusive ? "\\ge" : ">")
    : (result.inclusive ? "\\le" : "<");

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Zapisujemy nierówność:</div>
      <BlockMath
        math={`${formatLinearLatex(data.a, data.b)}${relation.latex}${data.targetValue}`}
      />
      <div><strong>Krok 2.</strong> Przenosimy wyraz wolny:</div>
      <BlockMath math={`${data.a}x${relation.latex}${rightSide}`} />
      <div>
        <strong>Krok 3.</strong> Dzielimy obie strony przez{" "}
        <InlineMath math={`${data.a}`} />.
        {data.a < 0 ? " Ponieważ dzielimy przez liczbę ujemną, zmieniamy zwrot nierówności." : ""}
      </div>
      <BlockMath math={`x${finalRelation}${data.boundary}`} />
      <div>
        Zatem <InlineMath math={`x\\in ${solutionLatex(data)}`} />.
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw;
  return raw
    ? <InlineMath math={intervalToLatex(raw, { withX: true })} />
    : <i>(brak odpowiedzi)</i>;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
